import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Download, Printer, QrCode, Copy, Check } from 'lucide-react';
import { HUB_PROPERTIES, HUB_PAGES, hubUrl } from './guest-hub/hubConfig';
import { Link } from 'react-router-dom';
import { QR_LOGO_DATA_URI } from './guest-hub/HubBrand';

const PNG_SIZE = 2048;


const triggerDownload = (href, filename) => {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const svgString = (id) => new XMLSerializer().serializeToString(document.getElementById(id));

export const downloadSVG = (id, name) => {
  const url = URL.createObjectURL(new Blob([svgString(id)], { type: 'image/svg+xml;charset=utf-8' }));
  triggerDownload(url, `${name}.svg`);
};

export const downloadPNG = (id, name) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = PNG_SIZE;
    canvas.height = PNG_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, PNG_SIZE, PNG_SIZE);
    ctx.drawImage(img, 0, 0, PNG_SIZE, PNG_SIZE);
    triggerDownload(canvas.toDataURL('image/png'), `${name}.png`);
  };
  img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString(id))))}`;
};

const QRTile = ({ prop, page }) => {
  const id = `qr-${prop.slug}-${page.key}`;
  const url = hubUrl(prop.slug, page.key);
  const fileName = `${prop.slug}-${page.key}-qr`;
  return (
    <div data-testid={`qr-tile-${prop.slug}-${page.key}`} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col items-center print:break-inside-avoid">
      <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-1">{prop.name}</p>
      <h3 className="text-lg font-semibold text-slate-900 mb-3">{page.label}</h3>
      <QRCodeSVG id={id} value={url} size={200} level="H" marginSize={2} bgColor="#ffffff" fgColor="#0f172a"
        imageSettings={{ src: QR_LOGO_DATA_URI, height: 48, width: 48, excavate: true }} />
      <a href={url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 mt-3 break-all text-center hover:text-amber-600">{url}</a>
      <div className="flex gap-2 mt-4 print:hidden">
        <Button data-testid={`qr-png-${prop.slug}-${page.key}`} size="sm" variant="outline" onClick={() => downloadPNG(id, fileName)}>
          <Download className="h-4 w-4 mr-1" />PNG
        </Button>
        <Button data-testid={`qr-svg-${prop.slug}-${page.key}`} size="sm" variant="outline" onClick={() => downloadSVG(id, fileName)}>
          <Download className="h-4 w-4 mr-1" />SVG
        </Button>
      </div>
    </div>
  );
};

const LinksTable = () => {
  const [copied, setCopied] = useState(false);
  const all = HUB_PROPERTIES.flatMap((p) => HUB_PAGES.map((pg) => `${p.name} – ${pg.label}: ${hubUrl(p.slug, pg.key)}`)).join('\n');
  const copyAll = () => {
    navigator.clipboard?.writeText(all);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div data-testid="qr-links-table" className="bg-white border border-slate-200 rounded-xl p-6 print:hidden">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">All Guest Hub links</h2>
        <Button data-testid="qr-copy-all-links" size="sm" onClick={copyAll} className="bg-slate-900 hover:bg-slate-800">
          {copied ? <><Check className="h-4 w-4 mr-1" />Copied</> : <><Copy className="h-4 w-4 mr-1" />Copy all links</>}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {HUB_PROPERTIES.map((p) => (
              <tr key={p.slug} className="border-t border-slate-100 align-top">
                <td className="py-3 pr-4 font-medium text-slate-900 whitespace-nowrap">{p.name}</td>
                <td className="py-3 space-y-1">
                  {HUB_PAGES.map((pg) => (
                    <div key={pg.key} className="text-slate-600 break-all"><span className="text-slate-400">{pg.label}:</span> {hubUrl(p.slug, pg.key)}</div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PropertyQRCodes = () => {
  const [selected, setSelected] = useState('all');
  const shown = selected === 'all' ? HUB_PROPERTIES : HUB_PROPERTIES.filter((p) => p.slug === selected);

  return (
    <div data-testid="qr-codes-page" className="min-h-screen bg-slate-50 pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 space-y-10">
        <div className="print:hidden">
          <div className="flex items-center gap-3 mb-3"><QrCode className="h-8 w-8 text-amber-500" />
            <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-900">Guest Hub QR Codes</h1>
          </div>
          <p className="text-slate-600 max-w-2xl">Permanent QR codes for every property and Guest Hub page. Download high-res PNG (2048px) for print or SVG for any size.</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {[{ slug: 'all', name: 'All properties' }, ...HUB_PROPERTIES].map((p) => (
              <Button key={p.slug} data-testid={`qr-filter-${p.slug}`} size="sm" onClick={() => setSelected(p.slug)}
                className={selected === p.slug ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'}>
                {p.name}
              </Button>
            ))}
            <Button data-testid="qr-print-btn" size="sm" variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-1" />Print
            </Button>
            <Link to="/qr-signs">
              <Button data-testid="qr-go-signs-btn" size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">Printable Signs</Button>
            </Link>
          </div>
        </div>
        {shown.map((p) => (
          <section key={p.slug} data-testid={`qr-section-${p.slug}`}>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{p.name}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {HUB_PAGES.map((pg) => <QRTile key={pg.key} prop={p} page={pg} />)}
            </div>
          </section>
        ))}
        <LinksTable />
      </div>
      <style>{`@media print { .print\\:hidden { display: none !important; } .print\\:break-inside-avoid { break-inside: avoid; } }`}</style>
    </div>
  );
};

export default PropertyQRCodes;
